import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import FolderSpecialRoundedIcon from "@mui/icons-material/FolderSpecialRounded";
import LocalPhoneRoundedIcon from "@mui/icons-material/LocalPhoneRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TravelExploreRoundedIcon from "@mui/icons-material/TravelExploreRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import {
  deleteRequest,
  getErrorMessage,
  getRequest,
  patchRequest,
  postRequest,
  PROJECTS_API,
} from "./api";
import {
  actionButtonSx,
  PageShell,
  panelSx,
  statusChip,
} from "./ProjectsCommon";

const emptyForm = {
  name: "",
  code: "",
  industry: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  status: "ACTIVE",
  notes: "",
};

const CUSTOMER_STATUS = ["ACTIVE", "INACTIVE"];

function MetricCard({ icon, title, value, caption, tone = "#0F172A" }) {
  return (
    <Card elevation={0} sx={{ ...panelSx, height: "100%" }}>
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.4} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2.5,
              display: "grid",
              placeItems: "center",
              color: tone,
              backgroundColor: "rgba(15,23,42,0.05)",
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize:{xs:10,md:15},
                fontWeight: 600,
                color: "#64748B",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontSize: 28,
                fontWeight: 950,
                color: "#0F172A",
                letterSpacing: "-0.04em",
              }}
            >
              {value}
            </Typography>
          </Box>
        </Stack>
        {caption ? (
          <Typography
            sx={{ mt: 1.2, color: "#64748B", fontSize: 13, lineHeight: 1.55 }}
          >
            {caption}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

function CustomersSkeleton() {
  const metricSkeletons = Array.from({ length: 4 });
  const customerSkeletons = Array.from({ length: 3 });
  const statSkeletons = Array.from({ length: 3 });

  return (
    <Stack spacing={2.25}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(4, 1fr)",
          },
          gap: 2,
          width: "100%",
        }}
      >
        {metricSkeletons.map((_, index) => (
          <Card key={index} elevation={0} sx={{ ...panelSx, height: "100%" }}>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" spacing={1.4} alignItems="center">
                <Skeleton
                  variant="rounded"
                  width={42}
                  height={42}
                  sx={{ borderRadius: 2.5 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="48%" height={18} />
                  <Skeleton variant="text" width="32%" height={42} />
                </Box>
              </Stack>
              <Skeleton
                variant="text"
                width="82%"
                height={22}
                sx={{ mt: 1.2 }}
              />
              <Skeleton variant="text" width="64%" height={20} />
            </CardContent>
          </Card>
        ))}
      </Box>

      <Paper elevation={0} sx={{ ...panelSx, p: 2 }}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
        >
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="34%" height={30} />
            <Skeleton variant="text" width="58%" height={22} />
          </Box>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.2}>
            <Skeleton
              variant="rounded"
              width={280}
              height={40}
              sx={{ maxWidth: "100%" }}
            />
            <Skeleton
              variant="rounded"
              width={180}
              height={40}
              sx={{ maxWidth: "100%" }}
            />
          </Stack>
        </Stack>
      </Paper>

      <Stack spacing={2}>
        {customerSkeletons.map((_, index) => (
          <Paper
            key={index}
            elevation={0}
            sx={{ ...panelSx, overflow: "hidden" }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  lg: "2fr 1fr",
                },
              }}
            >
              <Box sx={{ p: 2.25 }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  spacing={1.5}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      flexWrap="wrap"
                    >
                      <Skeleton variant="text" width="38%" height={34} />
                      <Skeleton
                        variant="rounded"
                        width={92}
                        height={26}
                        sx={{ borderRadius: 999 }}
                      />
                    </Stack>
                    <Skeleton variant="text" width="42%" height={22} />
                  </Box>
                  <Skeleton
                    variant="rounded"
                    width={118}
                    height={36}
                    sx={{ borderRadius: 2 }}
                  />
                </Stack>

                <Box
                  sx={{
                    mt: 1.4,
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(3, 1fr)",
                    },
                    gap: 1.4,
                  }}
                >
                  {statSkeletons.map((_, statIndex) => (
                    <Box
                      key={statIndex}
                      sx={{
                        p: 1.35,
                        borderRadius: 2,
                        backgroundColor: "#F8FAFC",
                        border: "1px solid #E2E8F0",
                      }}
                    >
                      <Skeleton variant="text" width="44%" height={18} />
                      <Skeleton
                        variant="text"
                        width="22%"
                        height={24}
                        sx={{ mt: 0.4 }}
                      />
                    </Box>
                  ))}
                </Box>

                <Divider sx={{ my: 1.7 }} />
                <Skeleton variant="text" width="92%" height={22} />
                <Skeleton variant="text" width="72%" height={22} />
              </Box>

              <Box
                sx={{
                  height: "100%",
                  p: 2,
                  background:
                    "linear-gradient(180deg, #F8FAFC 0%, #EEF2F7 100%)",
                  borderLeft: { lg: "1px solid #E2E8F0" },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Skeleton variant="text" width="42%" height={18} />
                  <Skeleton
                    variant="text"
                    width="54%"
                    height={28}
                    sx={{ mt: 0.8 }}
                  />
                  <Stack spacing={0.9} sx={{ mt: 1.4 }}>
                    <Skeleton variant="text" width="76%" height={22} />
                    <Skeleton variant="text" width="58%" height={22} />
                  </Stack>
                </Box>

                <Stack direction="row" spacing={1.1} sx={{ mt: 2 }}>
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={38}
                    sx={{ borderRadius: 2 }}
                  />
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={38}
                    sx={{ borderRadius: 2 }}
                  />
                </Stack>
              </Box>
            </Box>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
}

export default function ProjectsCustomers() {
  const { activeOrg } = useAuth();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");

  const validate = () => {
    const newErrors = {};

    if (!form.name) newErrors.name = "Required";
    if (!form.code) newErrors.code = "Required";
    if (!form.contact_email) {
      newErrors.contact_email = "Required";
    } else if (!/\S+@\S+\.\S+/.test(form.contact_email)) {
      newErrors.contact_email = "Invalid email format";
    }

    if (!form.status) newErrors.status = "Status is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const loadCustomers = useCallback(async () => {
    if (!token || !activeOrg?.id) return;
    setLoading(true);
    setError("");
    try {
      const data = await getRequest(
        `${PROJECTS_API}/customers`,
        token,
        activeOrg,
      );
      setCustomers(data?.customers || []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load customers"));
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    if (!error && !success) return undefined;

    const timer = window.setTimeout(() => {
      setError("");
      setSuccess("");
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [error, success]);

  const visibleCustomers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return customers.filter((customer) => {
      if (statusFilter !== "ALL" && customer.status !== statusFilter)
        return false;
      if (!q) return true;
      const haystack = [
        customer.name,
        customer.code,
        customer.industry,
        customer.contact_name,
        customer.contact_email,
        customer.contact_phone,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [customers, search, statusFilter]);

  const metrics = useMemo(() => {
    const active = visibleCustomers.filter(
      (item) => item.status === "ACTIVE",
    ).length;
    const projects = visibleCustomers.reduce(
      (sum, item) => sum + Number(item.summary?.project_count || 0),
      0,
    );
    const sites = visibleCustomers.reduce(
      (sum, item) => sum + Number(item.summary?.site_count || 0),
      0,
    );
    return { active, projects, sites };
  }, [visibleCustomers]);

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setErrors({});
    setOpen(true);
  };

  const handleEdit = (customer) => {
    setEditingId(customer.id);
    setForm({
      name: customer.name || "",
      code: customer.code || "",
      industry: customer.industry || "",
      contact_name: customer.contact_name || "",
      contact_email: customer.contact_email || "",
      contact_phone: customer.contact_phone || "",
      status: customer.status || "ACTIVE",
      notes: customer.notes || "",
    });
    setErrors({});
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    if (!form.name.trim() || !form.code.trim()) {
      setError("Customer name and code are required");
      return;
    }
    try {
      if (editingId) {
        await patchRequest(
          `${PROJECTS_API}/customers/${editingId}`,
          form,
          token,
          activeOrg,
        );
        setSuccess("Customer updated");
      } else {
        await postRequest(`${PROJECTS_API}/customers`, form, token, activeOrg);
        setSuccess("Customer created");
      }
      setOpen(false);

      await loadCustomers();
    } catch (err) {
      const message = getErrorMessage(err, "Unable to save customer");
      if (message.toLowerCase().includes("already")) {
        setErrors((prev) => ({
          ...prev,
          name: "name already exists",
          code: "code already exists",
          contact_email: "Email already exists",
        }));
      } else {
        setError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCustomer) return;
    setDeleting(true);
    // if (!window.confirm(`Delete customer ${customer.name}?`)) return;
    try {
      await deleteRequest(
        `${PROJECTS_API}/customers/${selectedCustomer.id}`,
        token,
        activeOrg,
      );
      setSuccess("Customer deleted");
      setDeleteOpen(false);
      setSelectedCustomer(null);
      await loadCustomers();
    } catch (err) {
      setError(getErrorMessage(err, "Unable to delete customer"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <PageShell
      title="Project Customers"
      subtitle="Manage deployment accounts, program ownership, rollout footprint, and customer contact readiness before projects move into field execution."
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={actionButtonSx}
        >
          Add Customer
        </Button>
      }
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      ) : null}

      {loading ? (
        <CustomersSkeleton />
      ) : (
        <Stack spacing={2.25}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(4, 1fr)",
              },
              gap: 2,
              width: "100%",
            }}
          >
            <MetricCard
              icon={<BusinessRoundedIcon />}
              title="Customers"
              value={visibleCustomers.length}
              caption="Deployment accounts in current view"
              tone="#2563EB"
            />

            <MetricCard
              icon={<VerifiedRoundedIcon />}
              title="Active"
              value={metrics.active}
              caption="Customers ready for rollout planning"
              tone="#16A34A"
            />

            <MetricCard
              icon={<FolderSpecialRoundedIcon />}
              title="Projects"
              value={metrics.projects}
              caption="Programs mapped to visible customers"
              tone="#7C3AED"
            />

            <MetricCard
              icon={<TravelExploreRoundedIcon />}
              title="Sites"
              value={metrics.sites}
              caption="Execution locations under visible customers"
              tone="#0F766E"
            />
          </Box>

          <Paper elevation={0} sx={{ ...panelSx, p: 2 }}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", lg: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Typography
                  sx={{ fontSize: 18, fontWeight: 900, color: "#0F172A" }}
                >
                  Customer Control Board
                </Typography>
                <Typography sx={{ mt: 0.35, color: "#64748B", fontSize: 13 }}>
                  Search customers by account, code, industry, contact, email,
                  or phone.
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.2}>
                <TextField
                  size="small"
                  placeholder="Search customers"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: search && (
                      <InputAdornment position="end">
                        <IconButton
                         size="small" onClick={() => setSearch("")}>
                          <Close fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: { md: 280 } }}
                />
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ minWidth: { md: 180 } }}
                >
                  <MenuItem value="ALL">All Statuses</MenuItem>
                  {CUSTOMER_STATUS.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Stack>
          </Paper>

          <Grid container spacing={2}>
            {visibleCustomers.map((customer) => (
              <Box sx={{ width: "100%" }} key={customer.id}>
                <Paper elevation={0} sx={{ ...panelSx, overflow: "hidden" }}>
                  {/* Main Layout */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        lg: "2fr 1fr",
                      },
                    }}
                  >
                    {/* Left Section */}
                    <Box>
                      <Box sx={{ p: 2.25 }}>
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          justifyContent="space-between"
                          spacing={1.5}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              flexWrap="wrap"
                            >
                              <Typography
                                sx={{
                                  fontSize: 22,
                                  fontWeight: 950,
                                  color: "#0F172A",
                                  letterSpacing: "-0.035em",
                                }}
                              >
                                {customer.name}
                              </Typography>
                              {statusChip(customer.status)}
                            </Stack>
                            <Typography
                              sx={{ mt: 0.45, color: "#64748B", fontSize: 13 }}
                            >
                              {customer.code} /{" "}
                              {customer.industry || "Industry not set"}
                            </Typography>
                          </Box>

                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="outlined"
                              onClick={() =>
                                navigate(
                                  `/projects/deployments?customer_id=${customer.id}`,
                                )
                              }
                              sx={{ fontWeight: 800, textTransform: "none" }}
                            >
                              View Projects
                            </Button>
                          </Stack>
                        </Stack>

                        {/* Inner Stats */}
                        <Box
                          sx={{
                            mt: 1.4,
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              md: "repeat(3, 1fr)",
                            },
                            gap: 1.4,
                          }}
                        >
                          <Box
                            sx={{
                              p: 1.35,
                              borderRadius: 2,
                              backgroundColor: "#F8FAFC",
                              border: "1px solid #E2E8F0",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: "#64748B",
                                fontWeight: 900,
                                textTransform: "uppercase",
                              }}
                            >
                              Projects
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.4,
                                fontWeight: 900,
                                color: "#0F172A",
                              }}
                            >
                              {customer.summary?.project_count || 0}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              p: 1.35,
                              borderRadius: 2,
                              backgroundColor: "#F8FAFC",
                              border: "1px solid #E2E8F0",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: "#64748B",
                                fontWeight: 900,
                                textTransform: "uppercase",
                              }}
                            >
                              Sites
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.4,
                                fontWeight: 900,
                                color: "#0F172A",
                              }}
                            >
                              {customer.summary?.site_count || 0}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              p: 1.35,
                              borderRadius: 2,
                              backgroundColor: "#F8FAFC",
                              border: "1px solid #E2E8F0",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: "#64748B",
                                fontWeight: 900,
                                textTransform: "uppercase",
                              }}
                            >
                              Account State
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.4,
                                fontWeight: 900,
                                color: "#0F172A",
                              }}
                            >
                              {customer.status || "UNKNOWN"}
                            </Typography>
                          </Box>
                        </Box>

                        <Divider sx={{ my: 1.7 }} />

                        <Typography
                          sx={{
                            color: "#475569",
                            fontSize: 13.5,
                            lineHeight: 1.65,
                          }}
                        >
                          {customer.notes || "No account notes captured yet."}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Right Section */}
                    <Box
                      sx={{
                        height: "100%",
                        p: 2,
                        background:
                          "linear-gradient(180deg, #F8FAFC 0%, #EEF2F7 100%)",
                        borderLeft: { lg: "1px solid #E2E8F0" },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: "#64748B",
                            fontWeight: 900,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                          }}
                        >
                          Primary Contact
                        </Typography>

                        <Typography
                          sx={{
                            mt: 0.8,
                            color: "#0F172A",
                            fontWeight: 900,
                            fontSize: 16,
                          }}
                        >
                          {customer.contact_name || "Not assigned"}
                        </Typography>

                        <Stack spacing={0.9} sx={{ mt: 1.4 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <EmailRoundedIcon
                              sx={{ color: "#2563EB", fontSize: 18 }}
                            />
                            <Typography sx={{ color: "#475569", fontSize: 13 }}>
                              {customer.contact_email || "Email not set"}
                            </Typography>
                          </Stack>

                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <LocalPhoneRoundedIcon
                              sx={{ color: "#0F766E", fontSize: 18 }}
                            />
                            <Typography sx={{ color: "#475569", fontSize: 13 }}>
                              {customer.contact_phone || "Phone not set"}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>

                      <Stack direction="row" spacing={1.1} sx={{ mt: 2 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<EditRoundedIcon />}
                          onClick={() => handleEdit(customer)}
                          sx={{ fontWeight: 850, textTransform: "none" }}
                        >
                          Edit
                        </Button>

                        <Button
                          fullWidth
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteOutlineRoundedIcon />}
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setDeleteOpen(true);
                          }}
                          sx={{ fontWeight: 850, textTransform: "none" }}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            ))}

            {!visibleCustomers.length ? (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{ ...panelSx, p: 5, textAlign: "center" }}
                >
                  <Typography
                    sx={{ fontSize: 20, fontWeight: 900, color: "#0F172A" }}
                  >
                    No customers match this view
                  </Typography>
                  <Typography sx={{ mt: 1, color: "#64748B" }}>
                    Adjust filters or register a new deployment customer.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreate}
                    sx={{ mt: 2, fontWeight: 800 }}
                  >
                    Add Customer
                  </Button>
                </Paper>
              </Grid>
            ) : null}
          </Grid>
        </Stack>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: panelSx }}
      >
        <DialogTitle
          sx={{
            fontWeight: 900,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          }}
        >
          {editingId ? "Edit Project Customer" : "Create Project Customer"}

          <IconButton
            onClick={() => setOpen(false)}
            sx={{
              color: "#fff",
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Customer Name"
              value={form.name}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, name: e.target.value }));
                setErrors((prev) => ({
                  ...prev,
                  name: error,
                  code: form ? "" : prev.code,
                }));
              }}
              error={!!errors.name}
              helperText={errors.name}
              required
              fullWidth
            />

            <TextField
              label="Customer Code"
              value={form.code}
              onChange={(e) => {
                setForm((prev) => ({
                  ...prev,
                  code: e.target.value.toUpperCase(),
                }));
                setErrors((prev) => ({
                  ...prev,
                  code: error,
                  code: form ? "" : prev.code,
                }));
              }}
              error={!!errors.code}
              helperText={errors.code}
              required
              fullWidth
            />

            <TextField
              label="Industry"
              value={form.industry}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, industry: e.target.value }))
              }
              fullWidth
            />

            <TextField
              label="Contact Name"
              value={form.contact_name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, contact_name: e.target.value }))
              }
              fullWidth
            />

            <TextField
              label="Contact Email"
              value={form.contact_email}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, contact_email: e.target.value }));
                setErrors((prev) => ({
                  ...prev,
                  contact_email: error,
                  contact_email: form ? "" : prev.contact_email,
                }));
              }}
              error={!!errors.contact_email}
              helperText={errors.contact_email}
              required
              fullWidth
            />

            <TextField
              label="Contact Phone"
              value={form.contact_phone}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, contact_phone: e.target.value }))
              }
              fullWidth
            />

            <TextField
              select
              label="Status"
              value={form.status}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, status: e.target.value }))
              }
              error={!!errors.status}
              helperText={errors.status}
              required
              fullWidth
            >
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="INACTIVE">INACTIVE</MenuItem>
            </TextField>

            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, notes: e.target.value }))
              }
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            sx={{
              background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
              position: "relative",
              minWidth: 140,
            }}
          >
            {saving ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : editingId ? (
              "Update Customer"
            ) : (
              "Create Customer"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      {/* delete model */}
      <Dialog
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900, color: "#DC2626" }}>
          Confirm Delete
        </DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{selectedCustomer?.name}</strong>?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ position: "relative", minWidth: 120 }}
          >
            {deleting ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
